// src/prototype/utils/weighting.ts
// ============================================================
// WEIGHTING UTILITIES
// Normalization and calculation functions for category and measure weights
// 
// UPDATED: January 2026 - Golden Demo Integration
// - calculateWeightedScore now handles ROI in raw numbers (not millions)
// - $5,000,000 raw = 100 score (was: 5 = 100)
//
// ─────────────────────────────────────────────────────────────────────────────
// `label` AND `name` ARE NOT FOR DISPLAY. Render displayName(measureId).
//
// These two tables predate the catalog display layer and carry their own labels, which
// are neither the catalog names nor the sanctioned display names. Three of them were
// terms not permitted in external copy — 'Stakeholder Engagement', 'Pilot Feasibility'
// and 'Data Transformation' — and they reached the Weights page because that surface read
// `label` straight out of here, bypassing the override table entirely.
//
// Every entry now carries `measureId`, the catalog id, which is the join to
// data/coverage/measureDisplayNames.displayName(). That accessor owns what reaches a
// screen. The label and name fields are kept because other code still keys on them, and
// because deleting them would hide the divergence rather than resolve it.
// ─────────────────────────────────────────────────────────────────────────────
// ============================================================

// ============================================================
// TYPES
// ============================================================

export interface CategoryWeight {
  id: string;
  name: string;
  shortName: string;
  weight: number; // 0-100 percentage
  dimension: 'BV' | 'EOI'; // Business Value or Ease of Implementation
  description: string;
}

export interface MeasureWeight {
  id: string;
  /** Catalog measure id. THE join to displayName; never render `name` directly. */
  measureId: number;
  /** Raw catalog-era label. NOT for display. See the header note on displayName. */
  name: string;
  categoryId: string;
  weight: number; // 0-100 percentage within category
  description: string;
}

export type CategoryWeightsMap = Record<string, number>;
export type MeasureWeightsMap = Record<string, Record<string, number>>;

// ============================================================
// LUCIDE ICON IMPORTS (for component usage)
// ============================================================
import { 
  TrendingUp, 
  Target, 
  Clock, 
  Database, 
  Shield, 
  Users,
  type LucideIcon,
} from 'lucide-react';

// ============================================================
// CATEGORY DEFINITIONS (with icons for UI)
// ============================================================

export interface CategoryDefinition {
  label: string;
  shortLabel: string;
  description: string;
  dimension: 'Business Value' | 'Ease of Implementation';
  icon: LucideIcon;
  defaultWeight: number;
}

export const CATEGORY_DEFINITIONS: Record<string, CategoryDefinition> = {
  costSavings: {
    label: 'Revenue Potential & Cost Savings',
    shortLabel: 'Revenue & Cost',
    description: 'Direct financial impact from implementation',
    dimension: 'Business Value',
    icon: TrendingUp,
    defaultWeight: 27, // acme-demo-v1 (matches ACME_DEMO_V1_WEIGHTS.financial)
  },
  strategicAlignment: {
    label: 'Alignment with Strategic Goals',
    shortLabel: 'Strategic Alignment',
    description: 'Fit with organizational mission and goals',
    dimension: 'Business Value',
    icon: Target,
    defaultWeight: 21, // acme-demo-v1 (matches ACME_DEMO_V1_WEIGHTS.strategic)
  },
  timeToValue: {
    label: 'Time-to-Value',
    shortLabel: 'Time-to-Value',
    description: 'Speed to realize benefits',
    dimension: 'Business Value',
    icon: Clock,
    defaultWeight: 10,
  },
  dataReadiness: {
    label: 'Data Readiness and Availability',
    shortLabel: 'Data Readiness',
    description: 'Quality and accessibility of required data',
    dimension: 'Ease of Implementation',
    icon: Database,
    defaultWeight: 14, // acme-demo-v1 (EOI category — 14×3 = feasibility 42)
  },
  compliance: {
    label: 'Regulatory and Compliance Considerations',
    shortLabel: 'Regulatory Compliance',
    description: 'Regulatory alignment and risk factors',
    dimension: 'Ease of Implementation',
    icon: Shield,
    defaultWeight: 14, // acme-demo-v1 (EOI category — 14×3 = feasibility 42)
  },
  orgReadiness: {
    label: 'Organizational Readiness and Capabilities',
    shortLabel: 'Org Readiness',
    description: 'Staff expertise and change management capacity',
    dimension: 'Ease of Implementation',
    icon: Users,
    defaultWeight: 14, // acme-demo-v1 (EOI category — 14×3 = feasibility 42)
  },
};

// ============================================================
// MEASURE DEFINITIONS (for UI display)
// ============================================================

export interface MeasureDefinition {
  key: string;
  /** Catalog measure id. THE join to displayName; never render `label` directly. */
  measureId: number;
  /** Raw catalog-era label. NOT for display. See the header note on displayName. */
  label: string;
  description: string;
  defaultWeight: number;
}

export const MEASURE_DEFINITIONS: Record<string, MeasureDefinition[]> = {
  costSavings: [
    { key: 'revenueGeneration', measureId: 1, label: 'Revenue Generation', description: 'Potential for new revenue streams', defaultWeight: 35 },
    { key: 'costSavings', measureId: 2, label: 'Cost Savings', description: 'Direct cost reduction opportunities', defaultWeight: 30 },
    { key: 'longTermValue', measureId: 3, label: 'Long-Term Value Creation', description: 'Sustainable competitive advantage', defaultWeight: 20 },
    { key: 'financialRisk', measureId: 4, label: 'Financial Risk Factors', description: 'Investment risk considerations', defaultWeight: 15 },
  ],
  strategicAlignment: [
    { key: 'coreMission', measureId: 5, label: 'Core Mission Support', description: 'Alignment with organizational mission', defaultWeight: 20 },
    { key: 'competitive', measureId: 6, label: 'Competitive Differentiation', description: 'Market positioning advantage', defaultWeight: 10 },
    { key: 'valueBased', measureId: 7, label: 'Value-Based Care Integration', description: 'Support for value-based models', defaultWeight: 10 },
    { key: 'strategicGrowth', measureId: 8, label: 'Strategic Growth', description: 'Long-term viability and growth', defaultWeight: 20 },
    { key: 'operationalEfficiency', measureId: 21, label: 'Operational Efficiency', description: 'Process improvement potential', defaultWeight: 20 },
    { key: 'techAlignment', measureId: 22, label: 'Technology Alignment', description: 'Fit with technology roadmap', defaultWeight: 10 },
    { key: 'revenueDiversification', measureId: 24, label: 'Revenue Diversification', description: 'New revenue stream potential', defaultWeight: 10 },
  ],
  timeToValue: [
    { key: 'implementationComplexity', measureId: 51, label: 'Implementation Complexity', description: 'Technical and operational complexity', defaultWeight: 17 },
    { key: 'marketReadiness', measureId: 52, label: 'Market Readiness', description: 'External market conditions', defaultWeight: 17 },
    { key: 'speedToReturns', measureId: 53, label: 'Speed to Generate Returns', description: 'Time to ROI realization', defaultWeight: 17 },
    { key: 'stakeholderEngagement', measureId: 54, label: 'Stakeholder Engagement', description: 'Stakeholder buy-in requirements', defaultWeight: 17 },
    { key: 'pilotFeasibility', measureId: 55, label: 'Pilot Feasibility', description: 'Ability to run controlled pilots', defaultWeight: 16 },
    { key: 'regulatorySpeed', measureId: 56, label: 'Regulatory Approval Speed', description: 'Time for compliance approvals', defaultWeight: 16 },
  ],
  dataReadiness: [
    { key: 'dataQuality', measureId: 26, label: 'Data Quality', description: 'Completeness and accuracy of data', defaultWeight: 14 },
    { key: 'dataStandardization', measureId: 27, label: 'Data Standardization', description: 'Consistency of data formats', defaultWeight: 14 },
    { key: 'dataAccessibility', measureId: 28, label: 'Data Accessibility', description: 'Ease of data access and integration', defaultWeight: 14 },
    { key: 'dataTransformation', measureId: 29, label: 'Data Transformation', description: 'Cleansing requirements', defaultWeight: 14 },
    { key: 'interoperability', measureId: 30, label: 'Interoperability', description: 'System connectivity', defaultWeight: 14 },
    { key: 'historicalData', measureId: 31, label: 'Historical Data', description: 'Availability of historical records', defaultWeight: 15 },
    { key: 'realTime', measureId: 32, label: 'Real-Time Capabilities', description: 'Live data feed availability', defaultWeight: 15 },
  ],
  compliance: [
    { key: 'securityPrivacy', measureId: 34, label: 'Security & Privacy', description: 'HIPAA and data security compliance', defaultWeight: 12 },
    { key: 'deIdentification', measureId: 35, label: 'De-Identification', description: 'Anonymization requirements', defaultWeight: 12 },
    { key: 'regulatoryFramework', measureId: 36, label: 'Regulatory Framework', description: 'Alignment with regulations', defaultWeight: 13 },
    { key: 'legalClarity', measureId: 37, label: 'Legal Clarity', description: 'Contractual requirements', defaultWeight: 13 },
    { key: 'auditability', measureId: 38, label: 'Auditability', description: 'Reporting and audit requirements', defaultWeight: 12 },
    { key: 'vendorCompliance', measureId: 39, label: 'Vendor Compliance', description: 'Third-party compliance', defaultWeight: 12 },
    { key: 'jurisdictional', measureId: 40, label: 'Jurisdictional Complexity', description: 'Multi-state/region considerations', defaultWeight: 13 },
    { key: 'patientConsent', measureId: 41, label: 'Patient Consent', description: 'Consent management requirements', defaultWeight: 13 },
  ],
  orgReadiness: [
    { key: 'techInfra', measureId: 42, label: 'Technical Infrastructure', description: 'IT systems and platforms', defaultWeight: 15 },
    { key: 'staffExpertise', measureId: 43, label: 'Staff Expertise', description: 'Team skills and knowledge', defaultWeight: 15 },
    { key: 'orgProcesses', measureId: 44, label: 'Organizational Processes', description: 'Workflow readiness', defaultWeight: 15 },
    { key: 'changeManagement', measureId: 45, label: 'Change Management', description: 'Adoption capacity', defaultWeight: 15 },
    { key: 'resourceAllocation', measureId: 46, label: 'Resource Allocation', description: 'Budget and staff availability', defaultWeight: 25 },
    { key: 'partnerships', measureId: 47, label: 'Partnerships', description: 'External collaboration readiness', defaultWeight: 15 },
  ],
};

// ============================================================
// DEFAULT CATEGORY WEIGHTS (legacy format - kept for compatibility)
// ============================================================

export const DEFAULT_CATEGORY_WEIGHTS: CategoryWeight[] = [
  {
    id: 'costSavings',
    name: 'Revenue Potential & Cost Savings',
    shortName: 'Revenue & Cost',
    weight: 27,
    dimension: 'BV',
    description: 'Direct financial impact from implementation',
  },
  {
    id: 'strategicAlignment',
    name: 'Alignment with Strategic Goals',
    shortName: 'Strategic Alignment',
    weight: 21,
    dimension: 'BV',
    description: 'Fit with organizational mission and goals',
  },
  {
    id: 'timeToValue',
    name: 'Time-to-Value',
    shortName: 'Time-to-Value',
    weight: 10,
    dimension: 'BV',
    description: 'Speed to realize benefits',
  },
  {
    id: 'dataReadiness',
    name: 'Data Readiness and Availability',
    shortName: 'Data Readiness',
    weight: 14,
    dimension: 'EOI',
    description: 'Quality and accessibility of required data',
  },
  {
    id: 'compliance',
    name: 'Regulatory and Compliance Considerations',
    shortName: 'Regulatory Compliance',
    weight: 14,
    dimension: 'EOI',
    description: 'Regulatory alignment and risk factors',
  },
  {
    id: 'orgReadiness',
    name: 'Organizational Readiness and Capabilities',
    shortName: 'Org Readiness',
    weight: 14,
    dimension: 'EOI',
    description: 'Staff expertise and change management capacity',
  },
];

// ============================================================
// DEFAULT MEASURE WEIGHTS (by category)
// ============================================================

export const DEFAULT_MEASURE_WEIGHTS: Record<string, MeasureWeight[]> = {
  costSavings: [
    { id: 'm1', measureId: 1, name: 'Revenue Generation', categoryId: 'costSavings', weight: 35, description: 'Potential for new revenue streams' },
    { id: 'm2', measureId: 2, name: 'Cost Savings', categoryId: 'costSavings', weight: 30, description: 'Direct cost reduction opportunities' },
    { id: 'm3', measureId: 3, name: 'Long-Term Value Creation', categoryId: 'costSavings', weight: 20, description: 'Sustainable competitive advantage' },
    { id: 'm4', measureId: 4, name: 'Financial Risk Factors', categoryId: 'costSavings', weight: 15, description: 'Investment risk considerations' },
  ],
  strategicAlignment: [
    { id: 'm5', measureId: 5, name: 'Core Mission Support', categoryId: 'strategicAlignment', weight: 20, description: 'Alignment with organizational mission' },
    { id: 'm6', measureId: 6, name: 'Competitive Differentiation', categoryId: 'strategicAlignment', weight: 10, description: 'Market positioning advantage' },
    { id: 'm7', measureId: 7, name: 'Value-Based Care Integration', categoryId: 'strategicAlignment', weight: 10, description: 'Support for value-based models' },
    { id: 'm8', measureId: 8, name: 'Strategic Growth', categoryId: 'strategicAlignment', weight: 20, description: 'Long-term viability and growth' },
    { id: 'm21', measureId: 21, name: 'Operational Efficiency', categoryId: 'strategicAlignment', weight: 20, description: 'Process improvement potential' },
    { id: 'm22', measureId: 22, name: 'Technology Alignment', categoryId: 'strategicAlignment', weight: 10, description: 'Fit with technology roadmap' },
    { id: 'm24', measureId: 24, name: 'Revenue Diversification', categoryId: 'strategicAlignment', weight: 10, description: 'New revenue stream potential' },
  ],
  timeToValue: [
    { id: 'm51', measureId: 51, name: 'Implementation Complexity', categoryId: 'timeToValue', weight: 17, description: 'Technical and operational complexity' },
    { id: 'm52', measureId: 52, name: 'Market Readiness', categoryId: 'timeToValue', weight: 17, description: 'External market conditions' },
    { id: 'm53', measureId: 53, name: 'Speed to Generate Returns', categoryId: 'timeToValue', weight: 17, description: 'Time to ROI realization' },
    { id: 'm54', measureId: 54, name: 'Stakeholder Engagement', categoryId: 'timeToValue', weight: 17, description: 'Stakeholder buy-in requirements' },
    { id: 'm55', measureId: 55, name: 'Pilot Feasibility', categoryId: 'timeToValue', weight: 16, description: 'Ability to run controlled pilots' },
    { id: 'm56', measureId: 56, name: 'Regulatory Approval Speed', categoryId: 'timeToValue', weight: 16, description: 'Time for compliance approvals' },
  ],
  dataReadiness: [
    { id: 'm26', measureId: 26, name: 'Data Quality', categoryId: 'dataReadiness', weight: 14, description: 'Completeness and accuracy of data' },
    { id: 'm27', measureId: 27, name: 'Data Standardization', categoryId: 'dataReadiness', weight: 14, description: 'Consistency of data formats' },
    { id: 'm28', measureId: 28, name: 'Data Accessibility', categoryId: 'dataReadiness', weight: 14, description: 'Ease of data access and integration' },
    { id: 'm29', measureId: 29, name: 'Data Transformation', categoryId: 'dataReadiness', weight: 14, description: 'Cleansing requirements' },
    { id: 'm30', measureId: 30, name: 'Interoperability', categoryId: 'dataReadiness', weight: 14, description: 'System connectivity' },
    { id: 'm31', measureId: 31, name: 'Historical Data', categoryId: 'dataReadiness', weight: 15, description: 'Availability of historical records' },
    { id: 'm32', measureId: 32, name: 'Real-Time Capabilities', categoryId: 'dataReadiness', weight: 15, description: 'Live data feed availability' },
  ],
  compliance: [
    { id: 'm34', measureId: 34, name: 'Security & Privacy', categoryId: 'compliance', weight: 12, description: 'HIPAA and data security compliance' },
    { id: 'm35', measureId: 35, name: 'De-Identification', categoryId: 'compliance', weight: 12, description: 'Anonymization requirements' },
    { id: 'm36', measureId: 36, name: 'Regulatory Framework', categoryId: 'compliance', weight: 13, description: 'Alignment with regulations' },
    { id: 'm37', measureId: 37, name: 'Legal Clarity', categoryId: 'compliance', weight: 13, description: 'Contractual requirements' },
    { id: 'm38', measureId: 38, name: 'Auditability', categoryId: 'compliance', weight: 12, description: 'Reporting and audit requirements' },
    { id: 'm39', measureId: 39, name: 'Vendor Compliance', categoryId: 'compliance', weight: 12, description: 'Third-party compliance' },
    { id: 'm40', measureId: 40, name: 'Jurisdictional Complexity', categoryId: 'compliance', weight: 13, description: 'Multi-state/region considerations' },
    { id: 'm41', measureId: 41, name: 'Patient Consent', categoryId: 'compliance', weight: 13, description: 'Consent management requirements' },
  ],
  orgReadiness: [
    { id: 'm42', measureId: 42, name: 'Technical Infrastructure', categoryId: 'orgReadiness', weight: 15, description: 'IT systems and platforms' },
    { id: 'm43', measureId: 43, name: 'Staff Expertise', categoryId: 'orgReadiness', weight: 15, description: 'Team skills and knowledge' },
    { id: 'm44', measureId: 44, name: 'Organizational Processes', categoryId: 'orgReadiness', weight: 15, description: 'Workflow readiness' },
    { id: 'm45', measureId: 45, name: 'Change Management', categoryId: 'orgReadiness', weight: 15, description: 'Adoption capacity' },
    { id: 'm46', measureId: 46, name: 'Resource Allocation', categoryId: 'orgReadiness', weight: 25, description: 'Budget and staff availability' },
    { id: 'm47', measureId: 47, name: 'Partnerships', categoryId: 'orgReadiness', weight: 15, description: 'External collaboration readiness' },
  ],
};

// ============================================================
// NORMALIZATION FUNCTIONS
// ============================================================

/**
 * Rebalances weights when one value changes, ensuring total = 100%
 * Uses proportional adjustment of other weights
 */
export function rebalanceCategoryWeights(
  currentWeights: CategoryWeightsMap,
  changedKey: string,
  newValue: number,
  minWeight = 5,
  maxWeight = 40
): CategoryWeightsMap {
  // Clamp the new value
  const clampedValue = Math.max(minWeight, Math.min(maxWeight, newValue));
  
  const oldValue = currentWeights[changedKey];
  const diff = clampedValue - oldValue;
  
  // Get other keys and their total
  const otherKeys = Object.keys(currentWeights).filter(k => k !== changedKey);
  const othersTotal = otherKeys.reduce((sum, k) => sum + currentWeights[k], 0);
  
  if (othersTotal === 0 || diff === 0) {
    return { ...currentWeights, [changedKey]: clampedValue };
  }
  
  const result: CategoryWeightsMap = { [changedKey]: clampedValue };
  let adjustedTotal = clampedValue;
  
  // Proportionally adjust others
  otherKeys.forEach((k, idx) => {
    const proportion = currentWeights[k] / othersTotal;
    let adjusted = currentWeights[k] - (diff * proportion);
    
    // Clamp to min/max
    adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));
    
    // For last item, ensure total = 100
    if (idx === otherKeys.length - 1) {
      adjusted = 100 - adjustedTotal;
      adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));
    }
    
    result[k] = Math.round(adjusted * 10) / 10; // Round to 1 decimal
    adjustedTotal += result[k];
  });
  
  // Final normalization pass to ensure exactly 100%
  return normalizeToTotal(result, 100);
}

/**
 * Normalizes weights to sum to a specific total (default 100)
 */
export function normalizeToTotal(
  weights: CategoryWeightsMap,
  targetTotal = 100
): CategoryWeightsMap {
  const currentTotal = Object.values(weights).reduce((sum, w) => sum + w, 0);
  
  if (currentTotal === 0) return weights;
  
  const factor = targetTotal / currentTotal;
  const result: CategoryWeightsMap = {};
  
  let runningTotal = 0;
  const keys = Object.keys(weights);
  
  keys.forEach((k, idx) => {
    if (idx === keys.length - 1) {
      // Last item gets the remainder to ensure exact total
      result[k] = Math.round((targetTotal - runningTotal) * 10) / 10;
    } else {
      result[k] = Math.round(weights[k] * factor * 10) / 10;
      runningTotal += result[k];
    }
  });
  
  return result;
}

/**
 * Rebalances measure weights within a category
 */
export function rebalanceMeasureWeights(
  currentWeights: Record<string, number>,
  changedKey: string,
  newValue: number,
  minWeight = 5,
  maxWeight = 50
): Record<string, number> {
  // Similar logic to category weights
  const clampedValue = Math.max(minWeight, Math.min(maxWeight, newValue));
  
  const oldValue = currentWeights[changedKey] || 0;
  const diff = clampedValue - oldValue;
  
  const otherKeys = Object.keys(currentWeights).filter(k => k !== changedKey);
  const othersTotal = otherKeys.reduce((sum, k) => sum + currentWeights[k], 0);
  
  if (othersTotal === 0 || diff === 0) {
    return { ...currentWeights, [changedKey]: clampedValue };
  }
  
  const result: Record<string, number> = { [changedKey]: clampedValue };
  let adjustedTotal = clampedValue;
  
  otherKeys.forEach((k, idx) => {
    const proportion = currentWeights[k] / othersTotal;
    let adjusted = currentWeights[k] - (diff * proportion);
    adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));
    
    if (idx === otherKeys.length - 1) {
      adjusted = 100 - adjustedTotal;
      adjusted = Math.max(minWeight, Math.min(maxWeight, adjusted));
    }
    
    result[k] = Math.round(adjusted * 10) / 10;
    adjustedTotal += result[k];
  });
  
  return normalizeToTotal(result, 100);
}

// ============================================================
// SCORING FUNCTIONS
// ============================================================

/**
 * Calculates a weighted score for an opportunity based on category weights
 * This is a simplified scoring for the prototype - production would use full measure scores
 * 
 * UPDATED: January 2026 - Golden Demo Integration
 * - ROI values are now in RAW NUMBERS (e.g., 5000000 = $5M)
 * - Previously assumed millions (e.g., 5 = $5M)
 * - Benchmark: $5,000,000 = score of 100
 */
export function calculateWeightedScore(
  opportunity: {
    roiEstimate: { low: number; high: number };
    implementationEffort: 'Low' | 'Medium' | 'High';
    timeToValue: string;
  },
  categoryWeights: CategoryWeightsMap
): number {
  // Proxy scores based on opportunity characteristics
  // In production, these would come from actual measure scores
  
  // Business Value dimension (costSavings + strategicAlignment + timeToValue)
  const avgRoi = (opportunity.roiEstimate.low + opportunity.roiEstimate.high) / 2;
  
  // UPDATED: ROI is now in raw numbers, not millions
  // $5,000,000 = 100 score (benchmark for "excellent" ROI)
  const ROI_BENCHMARK = 5000000;
  const roiScore = Math.min(100, (avgRoi / ROI_BENCHMARK) * 100);
  
  // Time to value score
  // Handle both "X-Y months" format and "X months" format
  const timeString = opportunity.timeToValue || '6';
  const months = parseInt(timeString.split('-')[0]) || parseInt(timeString) || 6;
  const timeScore = Math.max(0, 100 - (months * 8)); // Faster = higher
  
  // Implementation ease (inverted - lower effort = higher score)
  const effortScores = { Low: 90, Medium: 60, High: 30 };
  const effortScore = effortScores[opportunity.implementationEffort] || 60;
  
  // Calculate weighted score
  const bvWeight = (categoryWeights.costSavings || 25) + 
                   (categoryWeights.strategicAlignment || 20) + 
                   (categoryWeights.timeToValue || 10);
  const eoiWeight = (categoryWeights.dataReadiness || 15) + 
                    (categoryWeights.compliance || 15) + 
                    (categoryWeights.orgReadiness || 15);
  
  // BV score is average of ROI and strategic value
  const bvScore = roiScore;
  
  // EOI score is based on effort (proxy)
  const eoiScore = (effortScore + timeScore) / 2;
  
  // Combined score
  const totalWeight = bvWeight + eoiWeight;
  const weightedScore = ((bvScore * bvWeight) + (eoiScore * eoiWeight)) / totalWeight;
  
  return Math.round(weightedScore * 10) / 10;
}

/**
 * Converts category weights map to default format
 */
export function getDefaultCategoryWeightsMap(): CategoryWeightsMap {
  const result: CategoryWeightsMap = {};
  Object.entries(CATEGORY_DEFINITIONS).forEach(([key, def]) => {
    result[key] = def.defaultWeight;
  });
  return result;
}

/**
 * Converts measure weights to nested map format
 */
export function getDefaultMeasureWeightsMap(): MeasureWeightsMap {
  const result: MeasureWeightsMap = {};
  
  Object.entries(MEASURE_DEFINITIONS).forEach(([categoryId, measures]) => {
    result[categoryId] = measures.reduce((acc, measure) => {
      acc[measure.key] = measure.defaultWeight;
      return acc;
    }, {} as Record<string, number>);
  });
  
  return result;
}

/**
 * Check if category weights match defaults
 */
export function areCategoryWeightsDefault(weights: CategoryWeightsMap): boolean {
  const defaults = getDefaultCategoryWeightsMap();
  return Object.keys(defaults).every(key => weights[key] === defaults[key]);
}

// ============================================================
// 6 → 4 DIMENSION BRIDGE  (STRUCTURAL MAP — NO WEIGHTS)
// ============================================================
// dva-stage groups the 38 active measures into 6 categories (m_categories) which
// roll up to 2 indices (category_type ∈ {Value, EOI}). The prototype drilldown UI
// renders 4 dimensions (useCaseContentTypes.DimensionId). This bridge is the single
// authored map from stage's 6-category / measure_id granularity onto those 4
// dimensions. It carries ONLY structure — category ids, measure ids, and dimension
// membership. No index/measure weights and no PVF curve points appear here or are
// derivable from here (weights live in the CategoryWeight/MeasureWeight defaults
// above and are never joined into this map).
//
//   Financial Impact          <- cat 1  Revenue Potential & Cost Savings   (m1-4)
//   Strategic Alignment       <- cat 2  Alignment with Strategic Goals     (m5-8,21,22,24)
//   Operational Urgency       <- cat 6  Time-to-Value                      (m51-56)
//   Implementation Feasibility<- cat 3 + cat 4 + cat 5
//                                       (Data Readiness m26-32,
//                                        Regulatory/Compliance m34-41,
//                                        Org Readiness m42-47)
//
// measure_id is sparse (1–56 with ~18 gaps); this map keys on the id, never on
// position. Composite scores are NOT recomputed from this map — total_score stays
// authoritative (client_use_case_scores.total_score); this bridge only shapes the
// per-dimension *display* breakdown.

/** The 4 drilldown dimensions (mirrors useCaseContentTypes.DimensionId, redeclared
 *  here to keep weighting.ts free of adapter imports). */
export type BridgeDimensionId = 'financial' | 'urgency' | 'strategic' | 'feasibility';

/** stage m_categories.category_id (1–6) → UI dimension. */
export const STAGE_CATEGORY_TO_DIMENSION: Record<number, BridgeDimensionId> = {
  1: 'financial',    // Revenue Potential & Cost Savings (Value)
  2: 'strategic',    // Alignment with Strategic Goals (Value)
  3: 'feasibility',  // Data Readiness and Availability (EOI)
  4: 'feasibility',  // Regulatory and Compliance Considerations (EOI)
  5: 'feasibility',  // Organizational Readiness and Capabilities (EOI)
  6: 'urgency',      // Time-to-Value (Value)
};

/** stage measure_id → category_id, for the 38 active measures (sparse 1–56). */
export const STAGE_MEASURE_TO_CATEGORY: Record<number, number> = {
  1: 1, 2: 1, 3: 1, 4: 1,
  5: 2, 6: 2, 7: 2, 8: 2, 21: 2, 22: 2, 24: 2,
  26: 3, 27: 3, 28: 3, 29: 3, 30: 3, 31: 3, 32: 3,
  34: 4, 35: 4, 36: 4, 37: 4, 38: 4, 39: 4, 40: 4, 41: 4,
  42: 5, 43: 5, 44: 5, 45: 5, 46: 5, 47: 5,
  51: 6, 52: 6, 53: 6, 54: 6, 55: 6, 56: 6,
};

/** stage measure_id → UI dimension (composition of the two maps above). */
export function dimensionForMeasureId(measureId: number): BridgeDimensionId | undefined {
  const cat = STAGE_MEASURE_TO_CATEGORY[measureId];
  return cat === undefined ? undefined : STAGE_CATEGORY_TO_DIMENSION[cat];
}

/** UI dimension → the stage measure_ids that roll into it (inverse of the map above). */
export const DIMENSION_TO_MEASURE_IDS: Record<BridgeDimensionId, number[]> = (() => {
  const out: Record<BridgeDimensionId, number[]> = { financial: [], urgency: [], strategic: [], feasibility: [] };
  Object.keys(STAGE_MEASURE_TO_CATEGORY)
    .map(Number)
    .sort((a, b) => a - b)
    .forEach((mid) => {
      const dim = dimensionForMeasureId(mid);
      if (dim) out[dim].push(mid);
    });
  return out;
})();